import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  LuChevronLeft,
  LuChevronRight,
  LuPanelRightClose,
  LuPanelRightOpen,
} from 'react-icons/lu';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/common/Button';
import {
  ROTATION_GET_STARTED_MESSAGE,
  ROTATION_INVALID_SCOPE_MESSAGE,
  ROTATION_LOCKED_OPEN_PLAN_MESSAGE,
  ROTATION_LOCKED_UNSAVED_MESSAGE,
  ROTATION_NO_PADDOCKS_MESSAGE,
} from 'constants/messageConstants';
import { FARM_KEY_PREFIX } from 'constants/routeConstants';
import {
  ROTATION_EARLIER_SEASONS_LABEL,
  ROTATION_LATER_SEASONS_LABEL,
  ROTATION_PLANNING_SEASON_RESET_LABEL,
  ROTATION_SEASON_COUNT,
  ROTATION_SEASONS_BEFORE_PLANNING,
  ROTATION_SUMMARY_HIDE_LABEL,
  ROTATION_SUMMARY_LABEL,
  ROTATION_SUMMARY_OPEN_STORAGE_KEY,
  ROTATION_SUMMARY_SHOW_LABEL,
  TREE_YEAR_ALL,
} from 'constants/uiConstants';
import type { IFarm } from 'interfaces/IFarm';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInput } from 'interfaces/IInput';
import type { IPaddock } from 'interfaces/IPaddock';
import type { InsightsScopeLevel } from 'pages/FarmsPage/Workspace/Insights/insightUtils';
import type { TipText } from 'pages/FarmsPage/Workspace/Insights/nutrition/TipContent';
import WorkspaceEmptyState from 'pages/FarmsPage/Workspace/WorkspaceEmptyState';
import BrowserStorageUtils from 'utils/BrowserStorageUtils';

import { getSelectedInputId } from '../../../../../redux/selectors/InputSelectors';
import {
  setRotationPastCategory,
  setRotationPlannerDetails,
} from '../../../../../redux/slices/InsightsSlice';
import type { RootState } from '../../../../../redux/Store';

import {
  computeRotationGrid,
  resolveRotationSeasons,
  type RotationCell,
  type RotationScope,
} from './computeRotationGrid';
import RotationGrid from './RotationGrid';
import RotationHelp from './RotationHelp';
import RotationSummaryPanel from './RotationSummaryPanel';
import RotationViewOptions, { PILL_BUTTON_CLASS } from './RotationViewOptions';

/** Whether this viewer last left the summary open — a per-browser convenience; open by default. */
const loadSummaryOpen = (): boolean => {
  try {
    return localStorage.getItem(ROTATION_SUMMARY_OPEN_STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
};

interface RotationViewProps {
  /** Plans in tree scope across every season — the lens picks its own. */
  inputs: IInput[];
  farms: IFarm[];
  gUnits: GUnit[];
  scopeLevel?: InsightsScopeLevel;
}

/**
 * Rotation planner lens (OP-2501) — every paddock in scope as a row,
 * grouped by farm, one column per year, so the advisor can lay out the
 * farm's rotation in one place. Setting a cell's crop, variety and sowing
 * creates or updates that paddock's plan and its Plan crop establishment
 * task. Years before the planning year can show Actual yield and sowing
 * (View options); editing always sets the plan.
 */
const RotationView: React.FC<RotationViewProps> = ({
  inputs,
  farms,
  gUnits,
  scopeLevel,
}) => {
  const dispatch = useDispatch();
  const {
    rotationPastCategory: pastCategory,
    rotationPlannerDetails: details,
  } = useSelector((state: RootState) => state.insightsReducer);
  const { treeYearFilter, activeTreeNodeKey, selectedPaddockId } = useSelector(
    (state: RootState) => state.farmReducer
  );
  const selectedInputId = useSelector(getSelectedInputId);

  // The tree's year is the season being planned; "All years" has none, so
  // it's this year.
  const planningSeason =
    treeYearFilter !== TREE_YEAR_ALL
      ? treeYearFilter
      : new Date().getFullYear();
  const [seasonOffset, setSeasonOffset] = useState(0);
  const seasons = useMemo(
    () =>
      resolveRotationSeasons(
        planningSeason - ROTATION_SEASONS_BEFORE_PLANNING + seasonOffset,
        ROTATION_SEASON_COUNT
      ),
    [planningSeason, seasonOffset]
  );

  // The summary's year — the planning year until the advisor picks another;
  // back to it when the tree's year changes or the picked year scrolls out of
  // view. The pick remembers the planning year it was made in for that reason.
  const [summaryPick, setSummaryPick] = useState<{
    season: number;
    planningSeason: number;
  } | null>(null);
  const shownSummarySeason =
    summaryPick &&
    summaryPick.planningSeason === planningSeason &&
    seasons.includes(summaryPick.season)
      ? summaryPick.season
      : planningSeason;
  const setSummarySeason = (season: number) =>
    setSummaryPick({ season, planningSeason });
  const [summaryOpen, setSummaryOpen] = useState(loadSummaryOpen);
  useEffect(() => {
    try {
      localStorage.setItem(
        ROTATION_SUMMARY_OPEN_STORAGE_KEY,
        String(summaryOpen)
      );
    } catch {
      // Remembering this is a convenience; the toggle works without it.
    }
  }, [summaryOpen]);

  const scope = useMemo((): RotationScope | null => {
    if (scopeLevel === 'org') return { level: 'org' };
    if (scopeLevel === 'farm' && activeTreeNodeKey) {
      return {
        level: 'farm',
        farmId: activeTreeNodeKey.replace(FARM_KEY_PREFIX, ''),
      };
    }
    if (
      (scopeLevel === 'paddock' || scopeLevel === 'input') &&
      selectedPaddockId
    ) {
      return { level: 'paddock', paddockId: selectedPaddockId };
    }
    return null;
  }, [activeTreeNodeKey, scopeLevel, selectedPaddockId]);

  const grid = useMemo(
    () =>
      scope
        ? computeRotationGrid({
            farms,
            inputs,
            seasons,
            planningSeason,
            pastCategory,
            scope,
            gUnits,
          })
        : null,
    [farms, gUnits, inputs, pastCategory, planningSeason, scope, seasons]
  );

  const paddocksById = useMemo(
    () =>
      new Map<string, IPaddock>(
        farms.flatMap((farm) =>
          (farm.paddocks ?? []).map((paddock) => [paddock.paddockId, paddock])
        )
      ),
    [farms]
  );

  // A plan open in Planning, or with unsaved edits there, is locked: the
  // Planning draft doesn't pick up saves made elsewhere, so its next save
  // would put the old values back (the same rule as the Nutrition lens).
  const lockReasonFor = useCallback(
    (cell: RotationCell): TipText | null => {
      if (cell.inputId === selectedInputId) {
        return ROTATION_LOCKED_OPEN_PLAN_MESSAGE;
      }
      if (BrowserStorageUtils.hasEditedInput(cell.inputId)) {
        return ROTATION_LOCKED_UNSAVED_MESSAGE;
      }
      return null;
    },
    [selectedInputId]
  );

  if (!scope || !grid) {
    return <WorkspaceEmptyState message={ROTATION_INVALID_SCOPE_MESSAGE} />;
  }

  const hasPaddocks = grid.farms.length > 0;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="px-1.5"
            aria-label={ROTATION_EARLIER_SEASONS_LABEL}
            title={ROTATION_EARLIER_SEASONS_LABEL}
            onClick={() => setSeasonOffset((offset) => offset - 1)}
          >
            <LuChevronLeft size={16} aria-hidden />
          </Button>
          <span className="min-w-[6rem] text-center text-sm font-semibold text-charcoal">
            {`${seasons[0]}–${seasons[seasons.length - 1]}`}
          </span>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="px-1.5"
            aria-label={ROTATION_LATER_SEASONS_LABEL}
            title={ROTATION_LATER_SEASONS_LABEL}
            onClick={() => setSeasonOffset((offset) => offset + 1)}
          >
            <LuChevronRight size={16} aria-hidden />
          </Button>
          {seasonOffset !== 0 && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="ml-1 px-2"
              onClick={() => setSeasonOffset(0)}
            >
              {ROTATION_PLANNING_SEASON_RESET_LABEL}
            </Button>
          )}
        </div>
        <div className="ml-auto flex items-center gap-1">
          {/* Always here, open or closed, so the summary can be brought back
              or put away whatever the window width. */}
          <button
            type="button"
            className={PILL_BUTTON_CLASS}
            aria-pressed={summaryOpen}
            onClick={() => setSummaryOpen((open) => !open)}
          >
            {summaryOpen ? (
              <LuPanelRightClose size={14} aria-hidden />
            ) : (
              <LuPanelRightOpen size={14} aria-hidden />
            )}
            {summaryOpen
              ? ROTATION_SUMMARY_HIDE_LABEL
              : ROTATION_SUMMARY_SHOW_LABEL}
          </button>
          <span className="h-4 border-l border-gray-medium" />
          <RotationViewOptions
            details={details}
            onDetailsChange={(next) =>
              dispatch(setRotationPlannerDetails(next))
            }
            planningSeason={planningSeason}
            pastCategory={pastCategory}
            onPastCategoryChange={(next) =>
              dispatch(setRotationPastCategory(next))
            }
          />
          <span className="h-4 border-l border-gray-medium" />
          <RotationHelp />
        </div>
      </div>

      {!hasPaddocks ? (
        <WorkspaceEmptyState message={ROTATION_NO_PADDOCKS_MESSAGE} />
      ) : (
        <>
          {!grid.hasAnyPlan && (
            <p className="w-fit max-w-full rounded bg-secondary-light px-3 py-2 text-sm text-charcoal">
              {ROTATION_GET_STARTED_MESSAGE}
            </p>
          )}
          {/* Summary beside the grid on very wide screens (1536px and up),
              under it otherwise, so the grid gets the width on a laptop. */}
          <div className="flex flex-col gap-4 2xl:flex-row 2xl:items-start">
            <div className="min-w-0 flex-1">
              <RotationGrid
                grid={grid}
                seasons={seasons}
                planningSeason={planningSeason}
                canEdit
                details={details}
                paddocksById={paddocksById}
                lockReasonFor={lockReasonFor}
              />
            </div>
            {summaryOpen && (
              <aside
                aria-label={ROTATION_SUMMARY_LABEL}
                className="2xl:sticky 2xl:top-0 2xl:w-[24rem] 2xl:flex-shrink-0"
              >
                <RotationSummaryPanel
                  grid={grid}
                  seasons={seasons}
                  season={shownSummarySeason}
                  onSeasonChange={setSummarySeason}
                  planningSeason={planningSeason}
                  pastCategory={pastCategory}
                  inputs={inputs}
                  gUnits={gUnits}
                />
              </aside>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default RotationView;
